import React, { useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import UIKitButton from './Button';
import UIKitIcon from './Icon';
import { ThemeColor } from '../theme';

interface UIKitFileUploadButtonMultipleProps {
  htmlId?: string;
  text: string;
  onClick: (formData: FormData) => void;
  onError: (error: string) => void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  title?: string;
  wrapperId?: string;
  color?: ThemeColor;
  /** estilos específicos para el botón (opcional) */
  buttonStyle?: React.CSSProperties;
}

const Wrapper = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'stretch',
});

const HiddenInput = styled('input')({
  display: 'none',
});

const MaxSize = 30 * 1024 * 1024; // 30 MB por archivo

const UIKitFileUploadButtonMultiple: React.FC<UIKitFileUploadButtonMultipleProps> = ({
  htmlId,
  text,
  onClick,
  onError,
  className,
  style,
  disabled = false,
  iconLeft = <UIKitIcon name="publish" size="s" />,
  iconRight,
  title,
  wrapperId,
  color,
  buttonStyle,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [filesAccumulated, setFilesAccumulated] = useState<File[]>([]);

  const themeColor: ThemeColor = color || 'primary';

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) {
      return;
    }

    const newFilesArray = Array.from(files);

    const oversize = newFilesArray.some((f) => f.size > MaxSize);
    if (oversize) {
      onError('oversize');
      e.target.value = '';
      return;
    }

    const updatedFiles = filesAccumulated.concat(newFilesArray);
    setFilesAccumulated(updatedFiles);

    const formData = new FormData();
    for (let i = 0; i < updatedFiles.length; i += 1) {
      formData.append('files', updatedFiles[i]);
    }

    e.target.value = '';
    onClick(formData);
  };

  return (
    <Wrapper className={className} id={wrapperId} style={style}>
      <UIKitButton
        htmlId={htmlId}
        text={text}
        variant="flat"
        color={themeColor}
        size="small"
        disabled={disabled}
        iconLeft={iconLeft}
        iconRight={iconRight}
        onClick={handleButtonClick}
        title={title}
        style={{
          borderRadius: 'inherit',
          ...(buttonStyle || {}),
        }}
      />

      <HiddenInput type="file" multiple ref={inputRef} onChange={upload} disabled={disabled} />
    </Wrapper>
  );
};

export default UIKitFileUploadButtonMultiple;
